import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CheckoutArticleGroup, CheckoutArticleModifier } from 'src/smoothr-web-app-core/models/CheckoutArticleGroup';
import { ArticleType, getArticleType, getIconByArticleType, numberD } from '../../../smoothr-web-app-core/utils/utils';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';

export enum CheckoutItemStatus {
	AVAILABLE,
	SELECTED,
	TAKEN,
	PAID,
}

@Component({
	selector: 'app-checkout-item',
	standalone: false,
	templateUrl: './checkout-item.component.html',
	styleUrls: ['./checkout-item.component.scss'],
})
export class CheckoutItemComponent {
	@Input() item: Partial<CheckoutArticleGroup> | null = null;
	@Input() participant: string | null = null; // current logged-in user id
	@Input() editable = false;
	@Output() itemToggle = new EventEmitter<string>();
	CheckoutItemStatus = CheckoutItemStatus;
	private repositoryService = inject(RepositoryService);
	private translate = inject(TranslateService);

	// === Derived getters ===

	get title(): string {
		return this.item?.name ?? '';
	}

	get qty(): number {
		return this.item?.quantity ?? 1;
	}

	get price(): string {
		if (!this.item?.price || numberD(this.item?.price) === 0) {
			return this.repositoryService.currencyFormat.format(0);
		}
		return this.repositoryService.currencyFormat.format(numberD(this.item?.price));
	}

	get hasDiscount(): boolean {
		return !!this.item?.appliedDiscount;
	}

	get discount(): string {
		if (!this.item?.appliedDiscount || numberD(this.item?.appliedDiscount) === 0) {
			return '';
		}
		return this.translate.instant('checkoutItem.discountLabel', {
			amount: this.repositoryService.currencyFormat.format(numberD(this.item?.appliedDiscount)),
		});
	}

	// item state for icon logic
	get state(): CheckoutItemStatus {
		if (this.item?.paid === true) {
			return CheckoutItemStatus.PAID;
		}
		if (this.item?.participant === this.participant) {
			return CheckoutItemStatus.SELECTED;
		}
		if (this.item?.participant && this.item?.participant !== this.participant) {
			return CheckoutItemStatus.TAKEN;
		}
		return CheckoutItemStatus.AVAILABLE;
	}

	get image(): string {
		return getIconByArticleType(getArticleType(this.item?.sku));
	}

	get isGamePlay(): boolean {
		const item = this.item;
		if (!item) return false;
		return (
			getArticleType(item.sku) === ArticleType.GAMEPLAY ||
			!!item.modifiers?.find(it => getArticleType(it.sku) === ArticleType.GAMEPLAY)
		);
	}

	modifierText(modifier: CheckoutArticleModifier): string {
		const price = numberD(modifier.price);
		if (price === 0) {
			return `${modifier.quantity}x ${modifier.name}`;
		}
		return `${modifier.quantity}x ${modifier.name} (+${this.repositoryService.currencyFormat.format(price)})`;
	}

	onItemToggle() {
		this.itemToggle.emit(this.item?.id ?? '');
	}
}
